// TypeScript interfaces mirroring the real Booking & Scheduling backend DTOs
// (AdminBooking / BookingService / BookingAvailability / BookingSettings / PublicBooking controllers).

export enum BookingStatus {
  Pending = 0,
  Confirmed = 1,
  Completed = 2,
  Cancelled = 3,
  NoShow = 4,
}

export enum DayOfWeekEnum {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export enum MeetingType {
  Online = 0,
  OnSite = 1,
  Phone = 2,
}

export const DAY_OF_WEEK_LABELS: Record<DayOfWeekEnum, string> = {
  [DayOfWeekEnum.Sunday]: 'Sunday',
  [DayOfWeekEnum.Monday]: 'Monday',
  [DayOfWeekEnum.Tuesday]: 'Tuesday',
  [DayOfWeekEnum.Wednesday]: 'Wednesday',
  [DayOfWeekEnum.Thursday]: 'Thursday',
  [DayOfWeekEnum.Friday]: 'Friday',
  [DayOfWeekEnum.Saturday]: 'Saturday',
};

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.Pending]: 'Pending',
  [BookingStatus.Confirmed]: 'Confirmed',
  [BookingStatus.Completed]: 'Completed',
  [BookingStatus.Cancelled]: 'Cancelled',
  [BookingStatus.NoShow]: 'No Show',
};

// ---- BookingService (/api/BookingService, /api/PublicBooking/services) ----

export interface BookingServiceResponseDto {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number | null;

  meetingType: MeetingType;
  meetingLocation: string | null;

  active: boolean;
  sortOrder: number;
  createdDate: string;
}

export interface CreateBookingServiceDto {
  name: string;
  description?: string;
  durationMinutes: number;
  price: number | null;

  meetingType: MeetingType;
  meetingLocation?: string | null;

  active: boolean;
  sortOrder: number;
}

export type UpdateBookingServiceDto = CreateBookingServiceDto;

// ---- BookingAvailability (/api/BookingAvailability) ----

export interface BookingAvailabilityResponseDto {
  id: string;
  dayOfWeek: DayOfWeekEnum;
  startTime: string; // TimeSpan "HH:mm:ss"
  endTime: string; // TimeSpan "HH:mm:ss"
  isAvailable: boolean;
}

export interface CreateBookingAvailabilityDto {
  dayOfWeek: DayOfWeekEnum;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

export type UpdateBookingAvailabilityDto = CreateBookingAvailabilityDto;

// ---- Bookings (/api/AdminBooking, /api/PublicBooking) ----

export interface CreateBookingDto {
  bookingServiceId: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone?: string;
  customerNotes?: string;
  bookingDate: string; // ISO 8601 DateTime
}

export interface CreatePublicBookingDto extends CreateBookingDto {
  captchaToken: string;
}

export interface UpdateBookingDto extends CreateBookingDto {
  adminNotes?: string;
}

export interface BookingResponseDto {
  id: string;
  bookingReference: string;
  bookingServiceId: string;
  serviceName: string;

  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string | null;
  customerNotes: string | null;

  bookingDate: string;
  bookingEndDate: string;

  meetingType: MeetingType;
  meetingLocation: string | null;
  meetingProvider: string | null;
  meetingUrl: string | null;

  status: BookingStatus;
  adminNotes: string | null;
  createdDate: string;
  confirmationSentAt: string | null;
  lastReminderSentAt: string | null;
  calendarSyncStatus: string | null;
  crmSyncStatus: string | null;
}

export interface CancelBookingDto {
  reason?: string;
}

export interface RescheduleBookingDto {
  newBookingDate: string; // ISO 8601 DateTime
}

export interface BookingConfirmationDto {
  bookingReference: string;
  serviceName: string;
  customerFirstName: string;
  customerLastName: string;
  bookingDate: string;
  bookingEndDate: string;
  status: BookingStatus;
  meetingType: MeetingType;
  meetingLocation: string | null;
}

// ---- Public availability (/api/PublicBooking/availability) ----

export interface AvailableSlotDto {
  startTime: string; // ISO 8601 DateTime
  endTime: string; // ISO 8601 DateTime
  isAvailable: boolean;
}

export interface AvailabilityResponseDto {
  date: string;
  timezone: string;
  slots: AvailableSlotDto[];
}
// ---- Settings (/api/BookingSettings) ----

export interface BookingSettingsResponseDto {
  id: string;
  defaultAppointmentDuration: number;
  bufferMinutes: number;
  timezone: string;
  advanceBookingDays: number;
  minimumNoticeHours: number;
  reminderHours: string; // comma-separated, e.g. "24,1"
  calendarSyncEnabled: boolean;
  crmSyncEnabled: boolean;
  isCalendarConnected: boolean;
  connectedCalendarProvider?: string;
  connectedCalendarEmail?: string;
  bookingSlug: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonColor?: string;
  accentColor?: string;
}

// Branding-only subset exposed by the public booking endpoint (no PII, mirrors publicJobBoardApi's pattern).
export interface PublicBookingBrandingDto {
  bookingSlug: string;

  companyName: string;
  logoUrl: string;

  primaryColor: string;
  secondaryColor: string;
  buttonColor: string;
  accentColor: string;
}

export interface CalendarConnectionResponse {
  connected: boolean;
  provider?: string;
  email?: string;
  connectionId?: string;
}

export interface CreateBookingSettingsDto {
  defaultAppointmentDuration: number;
  bufferMinutes: number;
  timezone: string;
  advanceBookingDays: number;
  minimumNoticeHours: number;
  reminderHours: string;
  calendarSyncEnabled: boolean;
  crmSyncEnabled: boolean;
  crmApiKey?: string;
  crmInstanceUrl?: string;
  bookingSlug: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonColor?: string;
  accentColor?: string;
}

export interface MessageResponseDto {
  message: string;
}

// ---- Client-side only (not sent to the API; used for local table filtering) ----

export interface BookingFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: BookingStatus | 'All';
  serviceId?: string;
  customerName?: string;
}

